/** Mapa coroplético dos 27 estados do Brasil — SVG inline, sem lib de mapa.
 *
 * GeoJSON simplificado embutido (`../geo/br-ufs.json`) + projeção equiretangular
 * própria. Reutilizado pela aba CAPAG (cores categóricas por nota) e pelo mapa de
 * calor do Raio-X (escala sequencial por métrica). O chamador decide a cor via
 * `fill`/`fillOpacity` e o tooltip via `title`.
 */
import { useMemo, type ReactNode } from 'react';
import brUfs from '../geo/br-ufs.json';

type Ring = [number, number][];
type Geometry =
  | { type: 'Polygon'; coordinates: Ring[] }
  | { type: 'MultiPolygon'; coordinates: Ring[][] };
interface Feature { type: 'Feature'; properties: { uf: string; nome: string | null }; geometry: Geometry }

const FEATURES = (brUfs as unknown as { features: Feature[] }).features;

interface Projecao { d: (f: Feature) => string; w: number; h: number }

function projetar(): Projecao {
  let minLon = Infinity; let maxLon = -Infinity; let minLat = Infinity; let maxLat = -Infinity;
  for (const f of FEATURES) {
    const polys = f.geometry.type === 'Polygon' ? [f.geometry.coordinates] : f.geometry.coordinates;
    for (const poly of polys) for (const ring of poly) for (const [lon, lat] of ring) {
      if (lon < minLon) minLon = lon; if (lon > maxLon) maxLon = lon;
      if (lat < minLat) minLat = lat; if (lat > maxLat) maxLat = lat;
    }
  }
  const meanLat = (minLat + maxLat) / 2;
  const kx = Math.cos((meanLat * Math.PI) / 180); // correção equiretangular
  const w = (maxLon - minLon) * kx;
  const h = maxLat - minLat;
  const px = (lon: number) => (lon - minLon) * kx;
  const py = (lat: number) => maxLat - lat; // flip vertical (SVG y p/ baixo)
  const d = (f: Feature) => {
    const polys = f.geometry.type === 'Polygon' ? [f.geometry.coordinates] : f.geometry.coordinates;
    let path = '';
    for (const poly of polys) for (const ring of poly) {
      ring.forEach(([lon, lat], i) => {
        path += `${i === 0 ? 'M' : 'L'}${px(lon).toFixed(3)} ${py(lat).toFixed(3)}`;
        if (i < ring.length - 1) path += ' ';
      });
      path += 'Z';
    }
    return path;
  };
  return { d, w, h };
}

export const UFS_MAPA: string[] = FEATURES.map((f) => f.properties.uf);
export const NOME_UF: Record<string, string> = Object.fromEntries(
  FEATURES.map((f) => [f.properties.uf, f.properties.nome || f.properties.uf])
);

export interface ChoroplethBRProps {
  /** Cor de preenchimento por UF (sigla). */
  fill: (uf: string) => string;
  /** Opacidade de preenchimento por UF (0–1); default 1. */
  fillOpacity?: (uf: string) => number;
  /** Conteúdo do tooltip nativo (<title>) por UF. */
  title: (uf: string) => ReactNode;
  /** UF destacada (contorno reforçado). */
  selected?: string;
  /** Clique num estado. */
  onSelect?: (uf: string) => void;
  ariaLabel?: string;
}

export function ChoroplethBR({ fill, fillOpacity, title, selected, onSelect, ariaLabel }: ChoroplethBRProps) {
  const proj = useMemo(projetar, []);
  const clicavel = Boolean(onSelect);
  return (
    <svg
      viewBox={`0 0 ${proj.w} ${proj.h}`}
      className="h-auto w-full"
      role="img"
      aria-label={ariaLabel || 'Mapa dos estados do Brasil'}
    >
      {FEATURES.map((f) => {
        const uf = f.properties.uf;
        const sel = selected === uf;
        return (
          <path
            key={uf}
            d={proj.d(f)}
            fill={fill(uf)}
            fillOpacity={fillOpacity ? fillOpacity(uf) : 1}
            stroke={sel ? 'rgb(var(--ds-text-primary))' : 'rgb(var(--ds-surface))'}
            strokeWidth={sel ? proj.w * 0.004 : proj.w * 0.0015}
            className={clicavel ? 'cursor-pointer transition-opacity hover:opacity-80' : undefined}
            onClick={onSelect ? () => onSelect(sel ? '' : uf) : undefined}
          >
            <title>{title(uf)}</title>
          </path>
        );
      })}
    </svg>
  );
}

export default ChoroplethBR;
